package dev.lingvo.site.pages.app.settings.agents.agentId

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.shared.AgentLogRowDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableAlign
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.pages.app.settings.loadWorkspaceProtectedResource
import dev.lingvo.site.pages.app.settings.reloadWorkspaceProtectedResource
import dev.lingvo.site.pages.app.settings.rememberWorkspaceAuthorityState
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.util.csvRow
import dev.lingvo.site.util.downloadText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/**
 * Every call one agent made, by tool and day — refusals included.
 *
 * **The refusals are the reason this page exists.** An agent that is quietly being denied looks
 * exactly like an agent nobody is using, and the difference is a workspace where somebody spends a
 * day debugging their own config. One line — "propose_translation, 0 allowed, 412 denied" — is the
 * whole diagnosis.
 *
 * CSV because the next question is usually asked in a spreadsheet next to somebody's own numbers.
 */
@Page("/app/settings/agents/{agentId}/log")
@Layout(".components.layouts.AppLayout")
@Composable
fun AgentLogPage() {
    val ctx = rememberPageContext()
    val agentId = ctx.route.params["agentId"].orEmpty()
    val workspaceContext = WorkspaceRequestContext.current()
    val agentClaim = WorkspaceActionClaim.capture(workspaceContext, WorkspacePrivilege.AGENTS)

    var refreshKey by rememberWorkspaceAuthorityState(workspaceContext, agentId) { mutableStateOf(0) }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        if (agentClaim == null) {
            PageHeader(t("app.settings.agents.agent_id.log.agent_log", "Agent log"), "")
            LAlert(
                title = t("app.settings.agents.agent_id.log.agent_activity_is_managed_by_an", "Agent activity is managed by an owner or admin"),
                description = t("app.settings.agents.agent_id.log.your_role_in_this_workspace_cannot", "Your role in this workspace cannot view agent calls or refusals."),
                variant = LAlertVariant.INFO,
            )
            return@Column
        }

        // The agent id stays a key — it selects *which* log this is — and the refresh counter
        // becomes a token, because a reload is not a different resource. As a key it emptied the
        // log table on every refresh of a page whose whole purpose is reading rows.
        val log = reloadWorkspaceProtectedResource(
            context = workspaceContext,
            privilege = WorkspacePrivilege.AGENTS,
            reloadToken = refreshKey,
            requestKeys = listOf(agentId, "agent-log"),
        ) { workspaceSlug -> ApiClient.agentLog(workspaceSlug, agentId) }
        val name = loadWorkspaceProtectedResource(
            context = workspaceContext,
            privilege = WorkspacePrivilege.AGENTS,
            requestKeys = listOf(agentId, "agent-name"),
        ) { workspaceSlug ->
            ApiClient.agents(workspaceSlug).firstOrNull { it.id == agentId }?.name
        }

        PageHeader(
            (name as? Async.Success)?.value ?: t("app.settings.agents.agent_id.log.agent_log", "Agent log"),
            t("app.settings.agents.agent_id.log.every_tool_call_allowed_and_refused", "Every tool call, allowed and refused, grouped by day."),
        )
        Link(path = "/app/settings/agents/$agentId") { Text(t("app.settings.agents.agent_id.log.back_to_the_connection", "Back to the connection")) }

        // A decided request that failed to reload stays on screen looking undecided. Saying so is
        // the difference between "the list is stale" and "your decision did not take".
        log.refreshError?.let { failure ->
            LAlert(
                t("app.settings.agents.agent_id.log.showing_the_last_log", "Showing the last log that loaded"),
                failure.readable(),
                modifier = Modifier.fillMaxWidth(),
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }

        LAsyncContent(
            log.state,
            rows = 5,
            loadingLabel = t("app.settings.agents.agent_id.log.loading_the_log", "Loading the log"),
            onRetry = { refreshKey++ },
        ) { log ->
            val rows = log?.rows.orEmpty()
            if (rows.isEmpty()) {
                LEmptyState(
                    t("app.settings.agents.agent_id.log.nothing_yet", "Nothing yet"),
                    t("app.settings.agents.agent_id.log.this_connection_has_not_made_a_2", "This connection has not made a call. If you expected one, check that the " +
                        "client is pointed at this workspace and using the right credential."),
                    Modifier.fillMaxWidth(),
                    LEmptyStateIcon.AUDIT,
                ) {}
            } else {
                val refused = rows.sumOf { it.denied }
                if (refused > 0) {
                    LMuted(
                        tf(
                            "app.settings.agents.agent_id.log.refused_of_calls",
                            "{refused} of {total} calls were refused. A refusal is usually a " +
                                "missing scope, a project not on its list, or a budget.",
                            "refused" to refused,
                            "total" to rows.sumOf { it.calls + it.denied },
                        ),
                    )
                }
                Row(Modifier.gap(Space.SM)) {
                    LButton(
                        t("app.settings.agents.agent_id.log.download_csv", "Download CSV"),
                        { downloadText(toCsv(rows), "agent-$agentId-log.csv", "text/csv;charset=utf-8") },
                        variant = LButtonVariant.SECONDARY,
                    )
                    LButton(t("app.settings.agents.agent_id.log.refresh", "Refresh"), { refreshKey++ }, variant = LButtonVariant.GHOST)
                }
                LTable(caption = t("app.settings.agents.agent_id.log.calls_by_tool_and_day", "Calls by tool and day")) {
                    LTableHead {
                        LTableRow {
                            listOf(
                                t("app.settings.agents.agent_id.log.column_day", "Day"),
                                t("app.settings.agents.agent_id.log.column_tool", "Tool"),
                            ).forEach { LTableHeaderCell { Text(it) } }
                            listOf(
                                t("app.settings.agents.agent_id.log.column_allowed", "Allowed"),
                                t("app.settings.agents.agent_id.log.column_refused", "Refused"),
                                t("app.settings.agents.agent_id.log.column_credits", "Credits"),
                            ).forEach { LTableHeaderCell(align = LTableAlign.END) { Text(it) } }
                        }
                    }
                    LTableBody {
                        rows.forEach { row ->
                            LTableRow {
                                LTableCell {
                                    P(Modifier.tabularNumbers().margin(0.px).toAttrs()) { Text(row.day) }
                                }
                                LTableHeaderCell(scope = LTableHeaderScope.ROW) {
                                    P(Modifier.monoFont().margin(0.px).toAttrs()) { Text(row.tool) }
                                }
                                Number(row.calls)
                                LTableCell(align = LTableAlign.END) {
                                    if (row.denied > 0) {
                                        LBadge(row.denied.toString(), variant = LBadgeVariant.WARNING)
                                    } else {
                                        P(Modifier.tabularNumbers().margin(0.px).toAttrs()) { Text("0") }
                                    }
                                }
                                Number(row.credits)
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * The same rows, as a file.
 *
 * Every field is quoted by [csvRow] rather than only the ones that look like they need it — a tool
 * name is a bare word until the day somebody ships one with a comma in it.
 */
private fun toCsv(rows: List<AgentLogRowDto>): String = buildString {
    append(csvRow(listOf("day", "tool", "allowed", "refused", "credits")))
    rows.forEach { row ->
        append(
            csvRow(
                listOf(row.day, row.tool, row.calls.toString(), row.denied.toString(), row.credits.toString()),
            ),
        )
    }
}

@Composable
private fun Number(value: Int) {
    // Grouped, for the reason `usage/Events.kt` states beside its own copy of this helper: a
    // five-digit count is misread without it.
    LTableCell { P(Modifier.tabularNumbers().margin(0.px).toAttrs()) { Text(formatCount(value)) } }
}

